/**
 * The projects overview (AIOE 0.66.0): everything the console's Projects list and Portfolio draw, for the signed-in
 * person, in one answer. One card per project they can read, as the workbench's Projects grid draws one (its name
 * and area, a line of purpose from its roadmap, the team faces, what is happening and its health in words, the
 * machines on it), the business areas with a summary each, the filing requests waiting on them, and what they may
 * create. AIOE's own contract; no workbench mirror.
 *
 *   GET /projects/v1/overview[?state=archived|all]   a person (a sign-in): {@link ProjectOverview}; an ETag, and 304
 *                                                     for an unchanged answer
 *
 * Cheap by construction: every read spans the projects at once where the store has such a read (runs by day, tasks
 * by project, the inbox, plans waiting, spend), the answer is kept per person for a short while, and a browser that
 * holds the same answer gets a 304. Who sees what is `GET /projects/v1`'s rule: the projects the person holds a role
 * in (an administrator or a portfolio manager every one; an area's managers their area's).
 */
import { z } from "zod";
import { areaManagerSchema, promotionSchema } from "./portfolio.js";
import { projectRoleSchema, projectStateSchema } from "./projects.js";
export const PROJECT_OVERVIEW_PATH = "/projects/v1/overview";
/** How many days of activity a card's strip covers, oldest first, today last. */
export const PROJECT_OVERVIEW_DAYS = 14;
/** How a project is doing, in words, with the status mark that goes beside them (never a colour alone). */
export const projectHealthMarkSchema = z.enum(["working", "online", "idle", "medium", "error", "disabled"]);
export const projectHealthSchema = z.object({
    mark: projectHealthMarkSchema,
    /** Two or three words: "Working now", "Active this week", "Runs failing", "Quiet for a month", "Archived". */
    words: z.string().max(60),
    /** One sentence saying why, for a hint. */
    why: z.string().max(300).optional(),
});
/** One face on a card: a member of a team the project uses (an agent's character or a person), or a person on the project. */
export const projectFaceSchema = z.object({
    name: z.string().max(200),
    human: z.boolean().optional(),
    /** A person in the organisation: their picture is `GET /people/v1/:subjectId/picture`. */
    subjectId: z.string().max(200).optional(),
    avatar: z.number().int().nonnegative().optional(),
    useCharacter: z.boolean().optional(),
    pictureUrl: z.string().max(2000).optional(),
    color: z.string().max(7).optional(),
});
export const projectOverviewCardSchema = z.object({
    id: z.string(),
    name: z.string(),
    role: projectRoleSchema,
    state: projectStateSchema,
    area: z.object({ id: z.string(), name: z.string() }).optional(),
    /** The first line of the project's roadmap vision, cut to a sentence; absent when nobody wrote one. */
    purpose: z.string().max(240).optional(),
    /** When a machine last reported one of its repositories. */
    lastSeenAt: z.string().optional(),
    archivedAt: z.string().optional(),
    repositories: z.number().int().nonnegative(),
    /** People and groups on the project. */
    members: z.number().int().nonnegative(),
    /** Teams and specialists granted to it. */
    teams: z.number().int().nonnegative(),
    /** The first three of them by name, each opening its own page (`/teams/:id`). */
    teamLinks: z.array(z.object({ id: z.string(), name: z.string() })).max(3),
    /** Up to six faces: the members of its teams, else its people, owners first. */
    faces: z.array(projectFaceSchema).max(6),
    /** Faces left out of `faces`. */
    moreFaces: z.number().int().nonnegative(),
    /** The machines that hold its repositories (workbenches and nodes), how many answer now, and the first few by name. */
    machines: z.object({ total: z.number().int().nonnegative(), online: z.number().int().nonnegative(), names: z.array(z.string()).max(3) }),
    /** Agent runs going now. */
    running: z.number().int().nonnegative(),
    /** Records in the person's inbox on it that wait on them (approvals, questions, decisions). */
    needsYou: z.number().int().nonnegative(),
    /** Plans drafted and waiting for approval. */
    plansWaiting: z.number().int().nonnegative(),
    tasks: z.object({ open: z.number().int().nonnegative(), blocked: z.number().int().nonnegative() }),
    /** Runs started each day, {@link PROJECT_OVERVIEW_DAYS} days, oldest first; the days are the answer's `days`. */
    activity: z.array(z.number().int().nonnegative()),
    /** Runs started in the last seven days, and how many of them failed. */
    week: z.object({ runs: z.number().int().nonnegative(), failed: z.number().int().nonnegative() }),
    /** Spend booked to it over the last 30 days, in US dollars; absent when nothing was booked. Shown in `money`. */
    spendUsd: z.number().nonnegative().optional(),
    health: projectHealthSchema,
    /** Other projects with the same repository: an owner merges them on the project's page. */
    sameRepositoryAs: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
});
export const projectOverviewAreaSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    costCode: z.string().optional(),
    /** Its managers, named; a group keeps its id for the directory to name. */
    managers: z.array(areaManagerSchema.extend({ name: z.string().optional() })),
    /** The caller manages it (a portfolio manager manages every area). */
    mine: z.boolean(),
    /** Its projects the caller can see, and the people on them (each person once). */
    projects: z.number().int().nonnegative(),
    people: z.number().int().nonnegative(),
    /** Spend booked to its projects over the last 30 days, in US dollars; absent when nothing was booked. */
    spendUsd: z.number().nonnegative().optional(),
    /** What waits on a manager: filing requests into it, and its projects whose health is failing or blocked. */
    attention: z.object({ filing: z.number().int().nonnegative(), projects: z.number().int().nonnegative() }),
});
export const projectOverviewPromotionSchema = promotionSchema.extend({
    projectName: z.string().optional(),
    areaName: z.string().optional(),
    requestedByName: z.string().optional(),
    /** The caller decides it (they manage the area); otherwise they asked for it and may withdraw it. */
    decides: z.boolean(),
});
export const projectOverviewSchema = z.object({
    /** ISO time it was built. */
    at: z.string(),
    /** The days of every card's `activity`, as `YYYY-MM-DD` (UTC), oldest first. */
    days: z.array(z.string()),
    projects: z.array(projectOverviewCardSchema),
    /** With `q`, `limit` or `before` asked for: where the next page of `projects` starts (absent when there is no more). */
    next: z.string().optional(),
    /** With a page asked for: how many projects match the search, whatever page `projects` is. */
    matching: z.number().int().nonnegative().optional(),
    /** Every business area (their names are the organisation's), each summarised over the projects the caller sees. */
    areas: z.array(projectOverviewAreaSchema),
    /** Pending filing requests the caller decides or asked for. */
    promotions: z.array(projectOverviewPromotionSchema),
    /** Archived projects the caller can see, counted, while the list leaves them out. */
    archived: z.number().int().nonnegative(),
    /** What the caller may do: make a project (an administrator anywhere, a portfolio manager, an area's managers in it). */
    can: z.object({
        admin: z.boolean(),
        portfolioManager: z.boolean(),
        /** Area ids the caller may file a new project under. */
        manages: z.array(z.string()),
        create: z.boolean(),
    }),
    /** How to show the US dollar amounts: the organisation's currency and rate. */
    money: z.custom((v) => !!v && typeof v === "object" && typeof v.perUsd === "number"),
});
//# sourceMappingURL=projectOverview.js.map